import { useEffect, useMemo, useState } from 'react';
import { isValid, parseISO, startOfDay } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ImagePlus } from 'lucide-react';
import { ApiError } from '@/shared/api/client';
import type { Banner } from '@/shared/types/banner';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Switch } from '@/shared/ui/switch';
import { DatePicker } from '@/shared/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { updateBanner, updateBannerImage } from '../api/use-banners';
import { toApiDateTime, toFormDate } from '../model/banner-image';
import { BannerImageCropper } from './banner-image-cropper';

const schema = z
  .object({
    title: z.string().min(1, 'Informe o título'),
    description: z.string().min(1, 'Informe a descrição'),
    url: z.string().url('URL inválida'),
    expiry: z.boolean(),
    entryDate: z.string().optional(),
    exitDate: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.expiry) return;
    if (!data.entryDate) {
      ctx.addIssue({ code: 'custom', message: 'Informe a data de entrada', path: ['entryDate'] });
    }
    if (!data.exitDate) {
      ctx.addIssue({ code: 'custom', message: 'Informe a data de saída', path: ['exitDate'] });
    }
    if (data.entryDate && data.exitDate && data.entryDate > data.exitDate) {
      ctx.addIssue({ code: 'custom', message: 'Saída deve ser após a entrada', path: ['exitDate'] });
    }
  });

type FormValues = z.infer<typeof schema>;

interface BannerEditDialogProps {
  banner: Banner | null;
  idPassbook: string;
  onClose: () => void;
  onSaved: () => void;
}

export function BannerEditDialog({ banner, idPassbook, onClose, onSaved }: BannerEditDialogProps) {
  const form = useForm<FormValues>({ resolver: zodResolver(schema) });
  const expiry = form.watch('expiry');
  const errors = form.formState.errors;
  const today = useMemo(() => startOfDay(new Date()), []);

  const [sourceSrc, setSourceSrc] = useState<string | null>(null);
  const [croppedFile, setCroppedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const resetImageState = () => {
    if (sourceSrc?.startsWith('blob:')) URL.revokeObjectURL(sourceSrc);
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    setSourceSrc(null);
    setCroppedFile(null);
    setPreview(null);
  };

  useEffect(() => {
    if (!banner) return;
    const hasSchedule = Boolean(banner.expiry || banner.exitDate);
    form.reset({
      title: banner.title ?? '',
      description: banner.description ?? '',
      url: banner.url ?? '',
      expiry: hasSchedule,
      entryDate: toFormDate(banner.entryDate),
      exitDate: toFormDate(banner.exitDate),
    });
    resetImageState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banner, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!banner) return;
    try {
      await updateBanner(idPassbook, banner._id, {
        title: values.title,
        description: values.description,
        url: values.url,
        expiry: values.expiry,
        entryDate: values.expiry ? toApiDateTime(values.entryDate) : null,
        exitDate: values.expiry ? toApiDateTime(values.exitDate) : null,
      });
      if (croppedFile) {
        await updateBannerImage(banner._id, croppedFile);
      }
      toast.success('Banner atualizado.');
      resetImageState();
      onClose();
      onSaved();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.data as { error?: string; message?: string } | undefined)?.error ??
            err.message)
          : null;
      toast.error(msg || 'Falha ao salvar banner.');
    }
  });

  const handleClose = () => {
    resetImageState();
    onClose();
  };

  return (
    <Dialog open={Boolean(banner)} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-4xl overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>Editar banner</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Imagem</Label>
            {sourceSrc && !croppedFile ? (
              <BannerImageCropper
                imageSrc={sourceSrc}
                onCropped={(file, url) => {
                  setCroppedFile(file);
                  setPreview(url);
                  if (sourceSrc.startsWith('blob:')) URL.revokeObjectURL(sourceSrc);
                  setSourceSrc(null);
                }}
                onCancel={() => {
                  if (sourceSrc.startsWith('blob:')) URL.revokeObjectURL(sourceSrc);
                  setSourceSrc(null);
                }}
              />
            ) : (
              <div className="space-y-2">
                <div className="glass overflow-hidden rounded-xl">
                  <img
                    src={preview ?? banner?.banner}
                    alt="Imagem do banner"
                    className="mx-auto max-h-56 w-full object-contain"
                  />
                </div>
                <label className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-border/70 bg-foreground/[0.04] px-4 py-2.5 text-sm font-medium text-foreground backdrop-blur-sm transition-colors hover:bg-foreground/[0.08]">
                  <ImagePlus className="size-4" /> Trocar imagem
                  <Input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (sourceSrc?.startsWith('blob:')) URL.revokeObjectURL(sourceSrc);
                      setSourceSrc(URL.createObjectURL(file));
                      setCroppedFile(null);
                      if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
                      setPreview(null);
                    }}
                  />
                </label>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="edit-title">Título</Label>
            <Input id="edit-title" {...form.register('title')} />
            {errors.title ? <p className="text-xs text-destructive">{errors.title.message}</p> : null}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="edit-desc">Descrição</Label>
            <Textarea id="edit-desc" rows={3} {...form.register('description')} />
            {errors.description ? (
              <p className="text-xs text-destructive">{errors.description.message}</p>
            ) : null}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="edit-url">Link</Label>
            <Input id="edit-url" placeholder="https://…" {...form.register('url')} />
            {errors.url ? <p className="text-xs text-destructive">{errors.url.message}</p> : null}
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border p-3">
            <div>
              <p className="text-sm font-medium">Expirar banner</p>
              <p className="text-xs text-muted-foreground">Datas de entrada e saída</p>
            </div>
            <Switch checked={expiry} onCheckedChange={(v) => form.setValue('expiry', v)} />
          </div>
          {expiry ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label>Data de entrada</Label>
                <Controller
                  name="entryDate"
                  control={form.control}
                  render={({ field }) => (
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Entrada"
                      className="w-full"
                      fromDate={(() => {
                        // Mantém datas já salvas no passado editáveis
                        if (field.value) {
                          const d = parseISO(field.value);
                          if (isValid(d) && d < today) return d;
                        }
                        return today;
                      })()}
                    />
                  )}
                />
                {errors.entryDate ? (
                  <p className="text-xs text-destructive">{errors.entryDate.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-2">
                <Label>Data de saída</Label>
                <Controller
                  name="exitDate"
                  control={form.control}
                  render={({ field }) => (
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Saída"
                      className="w-full"
                      fromDate={(() => {
                        const entry = form.watch('entryDate');
                        if (!entry) return today;
                        const d = parseISO(entry);
                        return isValid(d) ? d : today;
                      })()}
                    />
                  )}
                />
                {errors.exitDate ? (
                  <p className="text-xs text-destructive">{errors.exitDate.message}</p>
                ) : null}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
